(function () {
  'use strict';

  // Several sections include this file with their own <script> tag, so the
  // same source can execute more than once per page — bail after the first
  // run to keep document-level listeners single.
  if (window.cgCarouselLoaded) return;
  window.cgCarouselLoaded = true;

  /* Shared scroll-snap carousel for cg-* landing sections.

     Markup contract (all hooks are data attributes so each section keeps
     its own class names / styling):
       [data-cg-carousel]                     root; optional data-cg-autoplay="<ms>"
         [data-cg-carousel-viewport]          scroll container (overflow-x + snap)
           [data-cg-carousel-track]           flex track, direct children = slides
         [data-cg-carousel-prev/next]         optional arrow buttons
         [data-cg-carousel-dots]              optional pagination container;
                                              value "numbers" renders 01/02/03
                                              buttons instead of plain dots.

     Pagination is per *page* (slides can share the viewport). Buttons get
     class `cg-carousel-dot` (+`cg-carousel-dot--number`) and `is-active`.
     Autoplay pauses on hover/focus/hidden-tab and is disabled entirely for
     prefers-reduced-motion visitors. */

  function scrollBehavior() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches
      ? 'auto'
      : 'smooth';
  }

  function initCarousel(root) {
    if (root.dataset.cgCarouselInit === '1') return;
    root.dataset.cgCarouselInit = '1';

    const viewport = root.querySelector('[data-cg-carousel-viewport]');
    const track = viewport && viewport.querySelector('[data-cg-carousel-track]');
    if (!viewport || !track) return;

    const slides = Array.prototype.slice.call(track.children);
    if (slides.length === 0) return;

    const prevBtn = root.querySelector('[data-cg-carousel-prev]');
    const nextBtn = root.querySelector('[data-cg-carousel-next]');
    const dotsWrap = root.querySelector('[data-cg-carousel-dots]');
    const numbered = dotsWrap && dotsWrap.getAttribute('data-cg-carousel-dots') === 'numbers';
    const goToLabel = root.getAttribute('data-cg-label-goto') || 'Go to slide';

    function step() {
      const styles = getComputedStyle(track);
      const gap = parseFloat(styles.columnGap || styles.gap || '0') || 0;
      return slides[0].getBoundingClientRect().width + gap;
    }

    const dots = [];

    function pageCount() {
      const s = step();
      if (s <= 0) return 1;
      const maxScroll = viewport.scrollWidth - viewport.clientWidth;
      if (maxScroll <= 2) return 1;
      return Math.ceil((maxScroll - 2) / s) + 1;
    }

    function goTo(page) {
      viewport.scrollTo({ left: step() * page, behavior: scrollBehavior() });
    }

    function buildDots() {
      if (!dotsWrap) return;
      const count = pageCount();
      if (dots.length === count) return;
      dotsWrap.innerHTML = '';
      dots.length = 0;
      for (let i = 0; i < count; i += 1) {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = numbered
          ? 'cg-carousel-dot cg-carousel-dot--number'
          : 'cg-carousel-dot';
        if (numbered) {
          dot.textContent = (i + 1 < 10 ? '0' : '') + (i + 1);
        }
        dot.setAttribute('aria-label', goToLabel + ' ' + (i + 1));
        dot.addEventListener(
          'click',
          (function (page) {
            return function () {
              stopAutoplay();
              goTo(page);
            };
          })(i)
        );
        dotsWrap.appendChild(dot);
        dots.push(dot);
      }
    }

    function currentPage() {
      const s = step();
      const maxScroll = viewport.scrollWidth - viewport.clientWidth - 2;
      const last = (dots.length || pageCount()) - 1;
      let idx = s > 0 ? Math.round(viewport.scrollLeft / s) : 0;
      if (viewport.scrollLeft >= maxScroll) idx = last;
      if (idx < 0) idx = 0;
      if (idx > last) idx = last;
      return idx;
    }

    function update() {
      const idx = currentPage();
      const atEnd = viewport.scrollLeft >= viewport.scrollWidth - viewport.clientWidth - 2;
      dots.forEach(function (dot, i) {
        dot.classList.toggle('is-active', i === idx);
      });
      if (prevBtn) prevBtn.disabled = viewport.scrollLeft <= 2;
      if (nextBtn) nextBtn.disabled = atEnd;

      // Controls living outside the slides (arrows, numbers) can restyle
      // per slide: mirror the active slide's data-cg-text onto the root.
      // Only meaningful for one-slide-per-page carousels; slides without
      // the attribute leave the root untouched.
      const activeSlide = slides[idx];
      if (activeSlide) {
        const textStyle = activeSlide.getAttribute('data-cg-text');
        if (textStyle) root.setAttribute('data-cg-active-text', textStyle);
      }
    }

    function refresh() {
      buildDots();
      update();
    }

    /* Autoplay — loops back to the first page at the end. */
    const autoplayMs = parseInt(root.getAttribute('data-cg-autoplay') || '0', 10);
    let timer = null;
    let stopped = false;

    function tick() {
      // The Theme Editor swaps section DOM on reload; a timer surviving on a
      // detached root would keep firing, so it clears itself here.
      if (!root.isConnected) {
        clearInterval(timer);
        return;
      }
      if (document.hidden || root.matches(':hover')) return;
      const last = (dots.length || pageCount()) - 1;
      const idx = currentPage();
      goTo(idx >= last ? 0 : idx + 1);
    }

    function startAutoplay() {
      if (
        stopped ||
        !autoplayMs ||
        slides.length < 2 ||
        window.matchMedia('(prefers-reduced-motion: reduce)').matches
      ) {
        return;
      }
      clearInterval(timer);
      timer = setInterval(tick, autoplayMs);
    }

    // Any manual interaction ends autoplay for good — resuming after the
    // visitor took control reads as the page fighting them.
    function stopAutoplay() {
      stopped = true;
      clearInterval(timer);
    }

    if (prevBtn) {
      prevBtn.addEventListener('click', function () {
        stopAutoplay();
        viewport.scrollBy({ left: -step(), behavior: scrollBehavior() });
      });
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', function () {
        stopAutoplay();
        viewport.scrollBy({ left: step(), behavior: scrollBehavior() });
      });
    }
    viewport.addEventListener('pointerdown', stopAutoplay);

    let raf;
    viewport.addEventListener('scroll', function () {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(update);
    });

    if (typeof ResizeObserver === 'function') {
      new ResizeObserver(refresh).observe(viewport);
    } else {
      window.addEventListener('resize', refresh);
    }

    refresh();
    startAutoplay();
  }

  function init(scope) {
    const ctx = scope && scope.querySelectorAll ? scope : document;
    ctx.querySelectorAll('[data-cg-carousel]').forEach(initCarousel);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', function (e) {
    init(e.target || document);
  });

  // Theme editor: selecting a slide block scrolls the carousel to it, so
  // per-slide styling (e.g. the hero nav's light/dark colors) previews
  // correctly instead of staying on the first slide.
  document.addEventListener('shopify:block:select', function (e) {
    const slide = e.target;
    const root = slide && slide.closest && slide.closest('[data-cg-carousel]');
    if (!root) return;
    const viewport = root.querySelector('[data-cg-carousel-viewport]');
    const track = viewport && viewport.querySelector('[data-cg-carousel-track]');
    if (!track || slide.parentElement !== track) return;
    const offset =
      slide.getBoundingClientRect().left - track.getBoundingClientRect().left;
    viewport.scrollTo({ left: offset, behavior: 'auto' });
  });
})();
